'use client';

import { useState, useEffect } from 'react';
import { useAppStore } from '@/stores/app-store';
import { hasPerm } from '@/lib/permissions';
import { isAddonEnabled } from '@/lib/entitlements';
import { t } from '@/lib/i18n';
import { DEV_TYPES, PLANT_TYPES, SOLUTION_TYPES } from '@/lib/device-types';
import { plantTypeLabel } from '../PlantIcon';
import LinkDeviceModal from '../modals/LinkDeviceModal';
import ThresholdConfigModal from '../modals/ThresholdConfigModal';
import NotifRuleModal from '../modals/NotifRuleModal';
import { loadNotifications } from '@/lib/notif-storage';
import MultiEmailSelect from '../MultiEmailSelect';
import LocationMap from '../LocationMap';
import { EffluentThresholds, DEFAULT_EFFLUENT } from '@/lib/types';
import { buildMockNotifications, MAX_NOTIF_RULES } from '@/lib/notif-mock';
import CallAlertCard from '@/components/CallAlertCard';

// Which notification-channel cards to show. Sites deliver via Telegram only, so
// the LINE and Email cards are hidden — flip a flag here to bring one back.
/*
 * ช่องทางที่เปิดให้ตั้งค่าได้
 * email ยังไม่เปิดเพราะยังไม่มีตัวส่งฝั่งเซิร์ฟเวอร์ (รอ MAIL_API_URL)
 */
const SHOW_CHANNEL = { line: true, telegram: true, call: true, email: false };

/* ── Inline SVG icons (14x14, premium 1.5 stroke) ── */
const IconGeneral = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="8" width="8" height="14" rx="1.5" /><rect x="14" y="3" width="8" height="19" rx="1.5" />
    <line x1="4.5" y1="11" x2="7.5" y2="11" /><line x1="4.5" y1="14" x2="7.5" y2="14" />
    <line x1="16.5" y1="6" x2="19.5" y2="6" /><line x1="16.5" y1="9" x2="19.5" y2="9" />
  </svg>
);

const IconDevices = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <circle cx="9" cy="9" r="1.5" /><circle cx="15" cy="9" r="1.5" />
    <circle cx="9" cy="15" r="1.5" /><circle cx="15" cy="15" r="1.5" />
    <path d="M4 12h-2M22 12h-2M12 4V2M12 22v-2" />
  </svg>
);

const IconAI = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="16" height="16" rx="4" />
    <circle cx="9" cy="10" r="1.5" fill="currentColor" /><circle cx="15" cy="10" r="1.5" fill="currentColor" />
    <path d="M9 16c1 1 5 1 6 0" />
    <line x1="12" y1="1" x2="12" y2="4" /><circle cx="12" cy="1" r="1" />
  </svg>
);

const IconNotif = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    <line x1="12" y1="2" x2="12" y2="4" />
  </svg>
);

const IconAccess = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="7" r="3.5" />
    <path d="M2 21v-1.5A4.5 4.5 0 0 1 6.5 15h5A4.5 4.5 0 0 1 16 19.5V21" />
    <circle cx="18" cy="8" r="2.5" />
    <path d="M18 14c2.5 0 4 1.5 4 3.5V19" />
  </svg>
);

const IconSave = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
  </svg>
);

const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const IconSun = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
  </svg>
);

const IconLightning = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const IconLeaf = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66L7 18" /><path d="M17 8c3-1.5 5-4 5-8-4 0-6.5 2-8 5" /><path d="M17 8c-3 2-5 5-5 9" />
  </svg>
);

const IconGear = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const IconPause = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" />
  </svg>
);

const IconPlay = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const IconTrashSmall = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const catIcons: Record<string, React.ReactNode> = {
  solar: <IconSun />,
  energy: <IconLightning />,
  environment: <IconLeaf />,
};

const tabIcons: Record<string, React.ReactNode> = {
  general: <IconGeneral />,
  devices: <IconDevices />,
  aiConfig: <IconAI />,
  notifications: <IconNotif />,
  access: <IconAccess />,
};

/* ── Premium Toggle Switch ── */
function ToggleSwitch({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        background: on ? 'var(--green)' : 'var(--bg-hover)',
        cursor: 'pointer',
        position: 'relative',
        transition: 'all .3s cubic-bezier(.16,1,.3,1)',
        flexShrink: 0,
        boxShadow: on ? '0 0 12px rgba(94,234,160,0.25)' : 'inset 0 1px 2px rgba(0,0,0,0.1)',
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#fff',
          position: 'absolute',
          top: 3,
          left: on ? 23 : 3,
          transition: 'left .3s cubic-bezier(.16,1,.3,1)',
          boxShadow: '0 1px 4px rgba(0,0,0,.15)',
        }}
      />
    </div>
  );
}

export default function PlantSettingsPage() {
  const currentUser = useAppStore((s) => s.currentUser);
  const currentPlant = useAppStore((s) => s.currentPlant);
  const plants = useAppStore((s) => s.plants);
  const lang = useAppStore((s) => s.lang);
  const th = lang === 'th';
  const psTab = useAppStore((s) => s.psTab);
  const setPsTab = useAppStore((s) => s.setPsTab);
  const updatePlant = useAppStore((s) => s.updatePlant);
  const deletePlant = useAppStore((s) => s.deletePlant);
  const toggleDeviceAI = useAppStore((s) => s.toggleDeviceAI);
  const toggleDeviceStatus = useAppStore((s) => s.toggleDeviceStatus);
  const unlinkDeviceFromPlant = useAppStore((s) => s.unlinkDeviceFromPlant);
  const updatePlantAIConfig = useAppStore((s) => s.updatePlantAIConfig);
  const savePlantAIConfig = useAppStore((s) => s.savePlantAIConfig);
  const updatePlantNotifications = useAppStore((s) => s.updatePlantNotifications);
  const updatePlantAccess = useAppStore((s) => s.updatePlantAccess);
  const toast = useAppStore((s) => s.toast);
  const dbUser = useAppStore((s) => s.dbUser);

  // On opening the Notifications tab, the DB is the source of truth (the 24/7 cron
  // reads it). Load from DB and sync the store/localStorage to it — so server-side
  // edits (schedule rules, threshold fixes) aren't clobbered by a stale local copy.
  // Only when the DB has nothing do we migrate the local copy up (one-time).
  useEffect(() => {
    if (psTab !== 'notifications' || !dbUser || !currentPlant) return;
    let cancelled = false;
    fetch(`/api/projects/${currentPlant}/notifications`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled) return;
        const dbNotif = j?.notifications;
        if (dbNotif && ((dbNotif.rules && dbNotif.rules.length) || dbNotif.channels)) {
          updatePlantNotifications(currentPlant, dbNotif);
        } else {
          const local = loadNotifications(currentPlant);
          if (local) fetch(`/api/projects/${currentPlant}/notifications`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notifications: local }) }).catch(() => {});
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [psTab, dbUser, currentPlant]);

  const plant = currentPlant ? plants[currentPlant] : null;

  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [solution, setSolution] = useState('');
  const [addr, setAddr] = useState('');
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeResults, setPlaceResults] = useState<{ name: string; lat: number; lng: number }[]>([]);
  const [searching, setSearching] = useState(false);
  const [accessList, setAccessList] = useState('');
  const [owner, setOwner] = useState('');
  const [savingAccess, setSavingAccess] = useState(false);
  // Merge the legacy viewer + admin lists into one access list (dedup, excluding
  // the owner who is shown separately).
  const mergeAccess = (v?: string | null, a?: string | null, exclude?: string | null) => {
    const ex = (exclude || '').trim().toLowerCase();
    return [...new Set([...(v || '').split(';'), ...(a || '').split(';')].map((s) => s.trim()).filter(Boolean))]
      .filter((e) => e.toLowerCase() !== ex)
      .join(';');
  };
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [showThreshold, setShowThreshold] = useState(false);
  const [thresholdDevIdx, setThresholdDevIdx] = useState(0);
  const [showAddRule, setShowAddRule] = useState(false);
  const [editRuleIdx, setEditRuleIdx] = useState<number | null>(null);
  const [tgTest, setTgTest] = useState<{ state: 'idle' | 'sending' | 'ok' | 'err'; msg?: string }>({ state: 'idle' });
  const [lineTest, setLineTest] = useState<{ state: 'idle' | 'sending' | 'ok' | 'err'; msg?: string }>({ state: 'idle' });
  const [aiSaving, setAiSaving] = useState(false);
  const [eff, setEff] = useState<EffluentThresholds>(DEFAULT_EFFLUENT);
  const [dormOn, setDormOn] = useState(false);

  useEffect(() => {
    if (plant) {
      setName(plant.name);
      setType(plant.type);
      setSolution(plant.solution || '');
      setDormOn(!!plant.dorm?.enabled);
      setAddr(plant.addr);
      setLat(typeof plant.lat === 'number' ? plant.lat : null);
      setLng(typeof plant.lng === 'number' ? plant.lng : null);
      setEff(plant.effluent ? { ...DEFAULT_EFFLUENT, ...plant.effluent } : DEFAULT_EFFLUENT);
      setPlaceResults([]);
      setPlaceQuery('');
      setAccessList(mergeAccess(plant.viewer, plant.admin, plant.owner));
      setOwner(plant.owner || '');
    }
  }, [plant]);

  if (!hasPerm(currentUser, 'psettings')) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted, #94a3b8)', fontSize: 14 }}>
        {t('accessDenied', lang)}
      </div>
    );
  }

  if (!currentPlant || !plant) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted, #94a3b8)', fontSize: 14 }}>
        {t('selectPlant', lang)}
      </div>
    );
  }

  /*
   * สิทธิ์แก้ไขต้องคิดจาก ACL ของ plant นี้ ไม่ใช่ userlevel ของบัญชี
   *
   * hasPerm(currentUser, 'psettings') ที่คุมเมนูอยู่อ่านจาก usertable.userlevel
   * ซึ่งเป็นค่าระดับบัญชี ไม่ได้บอกอะไรเกี่ยวกับไซต์ที่กำลังเปิด บัญชีที่เป็น
   * '[admin]' จึงเห็นฟอร์มแก้ไขของทุกไซต์รวมถึงไซต์ที่ตัวเองเป็นแค่ viewer
   * แล้วมีแต่ API คอยปฏิเสธอยู่ชั้นเดียว
   *
   * PUT /api/projects/[id] ยอมให้เฉพาะ owner หรือคนในคอลัมน์ admin (คั่นด้วย
   * ; หรือ ,) — เทียบเกณฑ์เดียวกันที่นี่ ค่านี้ตัดสินว่าจะแสดงหน้าตั้งค่าหรือ
   * แสดงแค่ว่าใครแชร์ไซต์นี้มา (ดู early return ด้านล่าง)
   *
   * โหมด demo ไม่มีคอลัมน์สิทธิ์และไม่มี server จึงแก้ได้เหมือน canDelete
   */
  const myEmail = (dbUser?.email ?? '').trim().toLowerCase();
  const plantAdmins = String(plant.admin ?? '')
    .split(/[;,]/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
  const canEditPlant = !dbUser
    || (plant.owner ?? '').trim().toLowerCase() === myEmail
    || plantAdmins.includes(myEmail);

  const devs = plant.devices || [];
  const aiConfig = plant.aiConfig || { enabled: true, notify: { app: true, telegram: true }, sensitivity: 'medium' as const, schedule: 'daily' as const };
  // Seed a realistic cold-chain mockup when the plant has no notifications yet
  // so the tab is demoable and editable. Once edited, plant.notifications is set
  // and this fallback is no longer used (see lib/notif-mock.ts).
  const notifications = plant.notifications || buildMockNotifications(devs, lang);
  const rulesFull = notifications.rules.length >= MAX_NOTIF_RULES;

  const ownerDirty = owner.trim().toLowerCase() !== (plant.owner || '').trim().toLowerCase();
  const accessDirty = accessList !== mergeAccess(plant.viewer, plant.admin, plant.owner) || ownerDirty;
  const saveAccess = async () => {
    if (!currentPlant) return;
    if (owner.trim() === '') { toast(lang === 'th' ? 'ต้องระบุเจ้าของ (Owner)' : 'Owner is required', 'error'); return; }
    setSavingAccess(true);
    const res = await updatePlantAccess(currentPlant, { viewer: accessList, admin: '', ...(ownerDirty ? { owner: owner.trim() } : {}) });
    setSavingAccess(false);
    if (res.ok) toast(t('saved', lang), 'success');
    else toast(`${t('saveFailed', lang)}: ${res.error || 'error'}`, 'error');
  };

  // AI Config tab hidden while the AI add-on is off (reserved for a future package).
  const tabs = ['general', 'devices', 'aiConfig', 'notifications', 'access'].filter(
    (tab) => tab !== 'aiConfig' || isAddonEnabled('ai'),
  );
  const tabLabels: Record<string, string> = {
    general: t('general', lang),
    devices: t('devices', lang),
    aiConfig: t('aiConfig', lang),
    notifications: t('notifications', lang),
    access: lang === 'th' ? 'สิทธิ์เข้าถึง' : 'Access',
  };

  const panelStyle: React.CSSProperties = {
    background: 'var(--bg-surface)',
    borderRadius: 16,
    border: '1px solid var(--glass-border)',
    overflow: 'hidden',
  };

  const headStyle: React.CSSProperties = {
    padding: '12px 16px',
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-subtle)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const bodyStyle: React.CSSProperties = {
    padding: 24,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 14px',
    fontSize: 13,
    borderRadius: 10,
    border: '1px solid var(--glass-border)',
    background: 'var(--bg-elevated)',
    color: 'var(--text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--text-muted)',
    marginBottom: 6,
    display: 'block',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  };

  const btnStyle: React.CSSProperties = {
    padding: '9px 22px',
    fontSize: 13,
    fontWeight: 700,
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    color: '#0C1222',
    background: 'linear-gradient(135deg, var(--amber), var(--gold))',
    boxShadow: '0 2px 12px var(--gold-glow)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const btnDeleteStyle: React.CSSProperties = {
    padding: '9px 22px',
    fontSize: 13,
    fontWeight: 700,
    borderRadius: 10,
    border: '1px solid var(--glass-border)',
    cursor: 'pointer',
    color: 'var(--red, #DC2626)',
    background: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const catColors: Record<string, { bg: string; text: string }> = {
    solar: { bg: 'rgba(245,166,35,0.15)', text: '#F5A623' },
    energy: { bg: 'rgba(59,130,247,0.15)', text: '#3B82F6' },
    environment: { bg: 'rgba(16,183,127,0.15)', text: '#10B77F' },
  };

  const doPlaceSearch = async () => {
    const q = placeQuery.trim();
    if (q.length < 2) return;
    setSearching(true);
    try {
      const res = await fetch(`/api/geocode?q=${encodeURIComponent(q)}`);
      const data = await res.json().catch(() => ({ results: [] }));
      setPlaceResults(Array.isArray(data.results) ? data.results : []);
    } catch {
      setPlaceResults([]);
    } finally {
      setSearching(false);
    }
  };

  const isWaterPlant = (() => { const p = (type || '').toLowerCase(); return p === 'water' || /waste|sewage|บำบัด/.test(p); })();

  const onSaveEffluent = async () => {
    updatePlant(currentPlant, { effluent: eff });
    try {
      await fetch(`/api/projects/${currentPlant}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config_data: { effluent: eff } }),
      });
    } catch { /* keep in-memory */ }
    toast(th ? 'บันทึกเกณฑ์มาตรฐานน้ำทิ้งแล้ว' : 'Effluent thresholds saved', 'success');
  };

  const onToggleDorm = async (next: boolean) => {
    setDormOn(next);
    const dorm = { ...(plant?.dorm || {}), enabled: next };
    updatePlant(currentPlant, { dorm });
    try {
      await fetch(`/api/projects/${currentPlant}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config_data: { dorm } }),
      });
    } catch { /* keep in-memory */ }
    toast(next ? (th ? 'เปิดโหมดหอพักแล้ว' : 'Dorm mode enabled') : (th ? 'ปิดโหมดหอพักแล้ว' : 'Dorm mode disabled'), 'success');
  };

  const onSave = async () => {
    // config_data is shallow-merged server-side, so sending { solution } keeps
    // meters/effluent intact.
    //
    // ⚠️ เดิมยิง fetch แบบทิ้งผลลัพธ์ (fire-and-forget + catch เปล่า) แล้วขึ้น
    // toast(t('save')) = "บันทึก" ทุกครั้ง โดน 403 ก็ยังบอกว่าบันทึกแล้ว
    // และอัปเดต store ไปก่อนยิง จึงเห็นค่าใหม่บนหน้าจอทั้งที่ DB ไม่ได้เปลี่ยน
    // กว่าจะรู้คือตอน refresh แล้วค่าเด้งกลับ — ย้าย updatePlant มาหลังสำเร็จ
    if (dbUser) {
      try {
        const res = await fetch(`/api/projects/${currentPlant}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, plant_type: type, address: addr, lat, lng, config_data: { solution: solution || null } }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          const why = res.status === 403
            ? (th ? 'ต้องเป็นเจ้าของหรือแอดมินของไซต์นี้' : 'owner or site admin only')
            : d.error || `HTTP ${res.status}`;
          toast(`${t('saveFailed', lang)}: ${why}`, 'error');
          return;
        }
      } catch (e) {
        toast(`${t('saveFailed', lang)}: ${e instanceof Error ? e.message : 'network'}`, 'error');
        return;
      }
    }
    updatePlant(currentPlant, { name, type, solution, addr, lat: lat ?? undefined, lng: lng ?? undefined });
    toast(t('saved', lang), 'success');
  };

  /*
   * Only the plant's owner may delete it, and the API enforces that with a plain
   * `LOWER(owner) = LOWER(?)` — the whole column against one email, not a search
   * through a list the way viewer/admin are matched. Mirror that comparison
   * exactly so the button is never offered to someone the server would 403.
   *
   * Demo mode has no owner column and no server, so the button stays.
   */
  const canDelete = !dbUser
    || (plant.owner ?? '').trim().toLowerCase() === (dbUser.email ?? '').trim().toLowerCase();

  const onDelete = async () => {
    // The plant name is in the prompt because this cannot be undone and the
    // generic "Confirm?" gave no clue which site was about to go.
    const ask = th
      ? `ลบ "${plant.name}" ถาวร? กู้คืนไม่ได้`
      : `Permanently delete "${plant.name}"? This cannot be undone.`;
    if (!confirm(ask)) return;
    const res = await deletePlant(currentPlant);
    if (!res.ok) toast(res.error || (th ? 'ลบไม่สำเร็จ' : 'Delete failed'), 'error');
  };

  // Determine which categories are present
  const categories = new Set<string>();
  devs.forEach((d) => {
    const dt = DEV_TYPES[d.type];
    if (dt) categories.add(dt.cat);
  });
  const onlineCount = devs.filter((d) => d.st === 'online').length;

  /*
   * ผู้ดูไม่เห็นหน้าตั้งค่าเลย — เห็นแค่ว่าใครแชร์ไซต์นี้มาให้
   *
   * ทุกแท็บในหน้านี้เป็นการตั้งค่าที่ผู้ดูกดไม่ได้ทั้งหมด (ข้อมูลไซต์ · อุปกรณ์ ·
   * AI · กฎแจ้งเตือน · สิทธิ์) การโชว์ฟอร์มที่แก้ไม่ได้ไม่ได้ให้ข้อมูลอะไรเพิ่ม
   * นอกจากความสับสน ส่วนที่ผู้ดูต้องรู้จริงคือ "ไซต์นี้มาจากใคร จะไปขอแก้กับใคร"
   *
   * ไม่โชว์รายชื่อผู้ดูคนอื่น — เป็นอีเมลของคนอื่นที่ไม่เกี่ยวกับงานของผู้ดู
   */
  if (!canEditPlant) {
    const rows: [string, React.ReactNode][] = [
      [th ? 'ไซต์' : 'Site', plant.name],
      [th ? 'เจ้าของ' : 'Owner', plant.owner || '—'],
      [th ? 'สิทธิ์ของคุณ' : 'Your access', th ? 'ผู้ดู — อ่านอย่างเดียว' : 'Viewer — read-only'],
      [th ? 'อุปกรณ์' : 'Devices', `${devs.length} · ${onlineCount} ${t('online', lang)}`],
    ];
    return (
      <div style={{ padding: 0, maxWidth: 560 }}>
        <div style={panelStyle}>
          <div style={headStyle}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="4" r="2" />
                <circle cx="4" cy="8" r="2" />
                <circle cx="12" cy="12" r="2" />
                <line x1="5.8" y1="7" x2="10.2" y2="5" />
                <line x1="5.8" y1="9" x2="10.2" y2="11" />
              </svg>
              {th ? 'ไซต์นี้ถูกแชร์ให้คุณ' : 'This site was shared with you'}
            </span>
          </div>
          <div style={bodyStyle}>
            {rows.map(([label, value]) => (
              <div
                key={label}
                style={{
                  display: 'flex', justifyContent: 'space-between', gap: 16,
                  padding: '10px 0', borderBottom: '1px solid var(--border-subtle)',
                  fontSize: 13,
                }}
              >
                <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 700, textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
              </div>
            ))}
            <div style={{ marginTop: 16, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              {th
                ? 'การตั้งค่าไซต์แก้ได้เฉพาะเจ้าของและแอดมินของไซต์ ถ้าต้องการเปลี่ยนชื่อ พิกัด อุปกรณ์ หรือกฎแจ้งเตือน ติดต่อเจ้าของข้างต้น'
                : 'Site settings can only be changed by the site owner or its admins. To change the name, location, devices or alert rules, contact the owner above.'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 0 }}>
      {/* Page title removed */}

      {/* Tab bar — premium glass */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          marginBottom: 24,
          padding: 3,
          borderRadius: 10,
          background: 'var(--bg-elevated)',
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setPsTab(tab)}
            style={{
              flex: 1,
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              background: psTab === tab ? 'var(--bg-surface)' : 'transparent',
              color: psTab === tab ? 'var(--accent)' : 'var(--text-muted)',
              boxShadow: psTab === tab ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all .3s cubic-bezier(.16,1,.3,1)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            {tabIcons[tab]}
            {tabLabels[tab]}
          </button>
        ))}
      </div>

      {/* General Tab */}
      {psTab === 'general' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Plant Info */}
          <div style={panelStyle}>
            <div style={headStyle}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="8" width="8" height="14" rx="1.5" /><rect x="14" y="3" width="8" height="19" rx="1.5" /><line x1="4.5" y1="11" x2="7.5" y2="11" /><line x1="16.5" y1="6" x2="19.5" y2="6" /><line x1="16.5" y1="9" x2="19.5" y2="9" /></svg>
                {t('plantInfo', lang)}
              </span>
            </div>
            <div style={bodyStyle}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>{t('plantName', lang)}</label>
                <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>{t('type', lang)}</label>
                <select style={inputStyle} value={type} onChange={(e) => setType(e.target.value)}>
                  {/*
                    ค่าที่เก็บไว้ไม่ตรงกับตัวเลือกใด ต้องมี option ของตัวเองด้วย
                    ไม่งั้น browser จะวาดตัวเลือกแรก (Home) ให้เห็นทั้งที่ค่าจริง
                    ยังเป็นของเดิม กด Save ก็เขียนค่าเดิมกลับไป คนใช้จึงแก้ไม่ได้
                    เลย — เจอกับ plant id 1·2·3 ที่ plant_type เป็น 'momospace'
                  */}
                  {!PLANT_TYPES.some((pt) => pt.id === type) && (
                    <option value={type}>
                      {plantTypeLabel(type)
                        ? `${plantTypeLabel(type)} · ${type}`
                        : th ? '— ไม่ระบุชนิด —' : '— unspecified —'}
                    </option>
                  )}
                  {PLANT_TYPES.map((pt) => (
                    <option key={pt.id} value={pt.id}>{pt.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Solution {th ? '· ระบบที่มอนิเตอร์' : '· monitored system'}</label>
                <select style={inputStyle} value={solution} onChange={(e) => setSolution(e.target.value)}>
                  <option value="">{th ? '— ไม่ระบุ —' : '— none —'}</option>
                  {SOLUTION_TYPES.map((s) => (
                    <option key={s.id} value={s.id}>{s.emoji} {s.name}</option>
                  ))}
                </select>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 4 }}>
                  {th ? 'กำหนดหน้า dashboard เริ่มต้นตามชนิดงาน' : 'Drives the default dashboard for this plant'}
                </div>
              </div>
              {/* Dormitory-mode add-on toggle (REQ: enable dorm workspace from UI) */}
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', borderRadius: 12, border: '1px solid var(--border)', background: dormOn ? 'color-mix(in srgb, var(--accent) 8%, transparent)' : 'var(--bg-elevated)' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>🏠 {th ? 'โหมดหอพัก (Add-on)' : 'Dormitory mode (Add-on)'}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3 }}>
                    {th ? 'เปิดพื้นที่จัดการห้อง/ผู้เช่า/มิเตอร์/บิล แทนเมนูมอนิเตอร์' : 'Replaces monitoring menus with rooms/tenants/meter/billing workspace'}
                  </div>
                </div>
                <button
                  onClick={() => onToggleDorm(!dormOn)}
                  aria-pressed={dormOn}
                  style={{ position: 'relative', width: 46, height: 26, borderRadius: 999, border: 'none', cursor: 'pointer', flexShrink: 0, background: dormOn ? 'var(--accent)' : 'color-mix(in srgb, var(--text-muted) 40%, transparent)', transition: 'background .15s' }}
                >
                  <span style={{ position: 'absolute', top: 3, left: dormOn ? 23 : 3, width: 20, height: 20, borderRadius: 999, background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
                </button>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>{t('address', lang)}</label>
                <input style={inputStyle} value={addr} onChange={(e) => setAddr(e.target.value)} />
              </div>

              {/* Location picker — search a place, click/drag the map, or type
                  coordinates. Saved with the plant via the projects PUT. */}
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>📍 {th ? 'ตำแหน่งที่ตั้ง' : 'Location'}</label>
                <div style={{ position: 'relative', marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      style={inputStyle}
                      placeholder={th ? 'ค้นหาสถานที่…' : 'Search for a place…'}
                      value={placeQuery}
                      onChange={(e) => setPlaceQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doPlaceSearch(); } }}
                    />
                    <button
                      onClick={doPlaceSearch}
                      disabled={searching}
                      style={{ ...btnStyle, padding: '9px 18px', whiteSpace: 'nowrap', opacity: searching ? 0.6 : 1 }}
                    >
                      {searching ? (th ? 'กำลังค้นหา…' : 'Searching…') : (th ? 'ค้นหา' : 'Search')}
                    </button>
                  </div>
                  {placeResults.length > 0 && (
                    <div style={{
                      position: 'absolute', zIndex: 500, top: '100%', left: 0, right: 0, marginTop: 4,
                      background: 'var(--bg-surface)', border: '1px solid var(--glass-border)', borderRadius: 10,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.18)', overflow: 'hidden', maxHeight: 240, overflowY: 'auto',
                    }}>
                      {placeResults.map((r, i) => (
                        <div
                          key={i}
                          onClick={() => { setLat(r.lat); setLng(r.lng); setPlaceResults([]); setPlaceQuery(r.name); }}
                          style={{ padding: '9px 12px', fontSize: 12, cursor: 'pointer', color: 'var(--text-secondary)', borderBottom: i < placeResults.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          {r.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--glass-border)' }}>
                  <LocationMap
                    height={300}
                    editable
                    selectedSn={currentPlant}
                    markers={lat != null && lng != null ? [{ sn: currentPlant, name: name || 'Plant', lat, lng, online: true, fields: [] }] : []}
                    onMapClick={(la, ln) => { setLat(la); setLng(ln); }}
                    onMarkerDragEnd={(_sn, la, ln) => { setLat(la); setLng(ln); }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>{th ? 'ละติจูด' : 'Latitude'}</label>
                    <input
                      style={inputStyle}
                      value={lat ?? ''}
                      inputMode="decimal"
                      placeholder="13.7563"
                      onChange={(e) => { const v = parseFloat(e.target.value); setLat(e.target.value === '' ? null : (isNaN(v) ? lat : v)); }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>{th ? 'ลองจิจูด' : 'Longitude'}</label>
                    <input
                      style={inputStyle}
                      value={lng ?? ''}
                      inputMode="decimal"
                      placeholder="100.5018"
                      onChange={(e) => { const v = parseFloat(e.target.value); setLng(e.target.value === '' ? null : (isNaN(v) ? lng : v)); }}
                    />
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  {th ? 'คลิกบนแผนที่เพื่อปักหมุด หรือกรอกพิกัดเอง' : 'Click on the map to set location, or enter coordinates manually'}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button style={btnStyle} onClick={onSave}>
                  <IconSave />
                  {t('save', lang)}
                </button>
                {canDelete && (
                  <button style={btnDeleteStyle} onClick={onDelete}>
                    <IconTrash />
                    {t('deletePlant', lang)}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Plant Summary */}
          <div style={panelStyle}>
            <div style={headStyle}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14" /><line x1="9" y1="21" x2="9" y2="8" /><line x1="14" y1="21" x2="14" y2="11" /><line x1="19" y1="21" x2="19" y2="4" /></svg>
                {t('plantSummary', lang)}
              </span>
            </div>
            <div style={bodyStyle}>
              {/* Solutions */}
              <div style={{ marginBottom: 18 }}>
                <div style={labelStyle}>{t('activeSolutions', lang)}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Array.from(categories).length === 0 ? (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No devices</span>
                  ) : (
                    Array.from(categories).map((cat) => {
                      const c = catColors[cat] || catColors.energy;
                      return (
                        <span
                          key={cat}
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '5px 12px',
                            borderRadius: 8,
                            background: c.bg,
                            color: c.text,
                            textTransform: 'capitalize',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            border: `1px solid ${c.text}18`,
                          }}
                        >
                          {catIcons[cat] || null}
                          {cat}
                        </span>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Stats grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div style={{ textAlign: 'center', padding: '14px 10px', borderRadius: 10, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--blue-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" /><circle cx="9" cy="9" r="1.5" /><circle cx="15" cy="9" r="1.5" /><circle cx="9" cy="15" r="1.5" /><circle cx="15" cy="15" r="1.5" /></svg>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--blue)' }}>{devs.length}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>Devices</div>
                </div>
                <div style={{ textAlign: 'center', padding: '14px 10px', borderRadius: 10, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--green-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="8.5 12.5 10.5 14.5 15.5 9.5" /></svg>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--green)' }}>{onlineCount}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>Online</div>
                </div>
                <div style={{ textAlign: 'center', padding: '14px 10px', borderRadius: 10, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: aiConfig.enabled ? 'var(--green-dim)' : 'var(--red-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={aiConfig.enabled ? 'var(--green)' : 'var(--red)'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="4" /><circle cx="9" cy="10" r="1.5" fill="currentColor" /><circle cx="15" cy="10" r="1.5" fill="currentColor" /><path d="M9 16c1 1 5 1 6 0" /></svg>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: aiConfig.enabled ? 'var(--green)' : 'var(--red)' }}>
                    {aiConfig.enabled ? 'ON' : 'OFF'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>AI</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Devices Tab */}
      {psTab === 'devices' && (
        <div style={panelStyle}>
          <div style={headStyle}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" /><circle cx="9" cy="9" r="1.5" /><circle cx="15" cy="9" r="1.5" /><circle cx="9" cy="15" r="1.5" /><circle cx="15" cy="15" r="1.5" /><path d="M4 12h-2M22 12h-2M12 4V2M12 22v-2" /></svg>
              {t('devicesInPlant', lang)}
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--gold-dim)', color: 'var(--gold)' }}>{devs.length}</span>
            </span>
            <button style={{ ...btnStyle, fontSize: 12, padding: '6px 16px' }} onClick={() => setShowAddDevice(true)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              {t('addDevice', lang)}
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            {devs.length === 0 ? (
              <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3, marginBottom: 12 }}>
                  <rect x="4" y="4" width="16" height="16" rx="2" /><circle cx="9" cy="9" r="1.5" /><circle cx="15" cy="9" r="1.5" /><circle cx="9" cy="15" r="1.5" /><circle cx="15" cy="15" r="1.5" />
                </svg>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t('noDevices', lang)}</div>
                <div style={{ fontSize: 11, marginTop: 4, opacity: 0.7 }}>Click &quot;+ Add Device&quot; to get started</div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    {['Status', 'Device', t('type', lang), 'S/N', 'AI'].map((h) => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--text-muted)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {devs.map((d, idx) => {
                    const dt = DEV_TYPES[d.type];
                    const dtColor = dt?.color || 'blue';
                    return (
                      <tr
                        key={d.sn}
                        style={{ borderBottom: '1px solid var(--border-subtle)', transition: 'background .15s' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <td style={{ padding: '12px 16px' }}>
                          <span className={`badge ${d.st === 'online' ? 'badge-on' : 'badge-off'}`}>
                            {d.st === 'online' ? 'ON' : 'OFF'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{d.name}</div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 6,
                            background: `var(--${dtColor}-dim)`, color: `var(--${dtColor})`,
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                          }}>
                            {dt?.name || d.type}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.02em' }}>{d.sn}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <button
                            onClick={() => toggleDeviceAI(currentPlant, idx)}
                            style={{
                              border: 'none', cursor: 'pointer', padding: '3px 10px', borderRadius: 6,
                              fontSize: 10, fontWeight: 700,
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              background: d.aiEnabled !== false ? 'var(--green-dim)' : 'var(--red-dim)',
                              color: d.aiEnabled !== false ? 'var(--green)' : 'var(--red)',
                              transition: 'all .2s',
                            }}
                          >
                            {d.aiEnabled !== false && (
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="4" y="4" width="16" height="16" rx="4" /><circle cx="9" cy="10" r="1.5" fill="currentColor" /><circle cx="15" cy="10" r="1.5" fill="currentColor" /><path d="M9 16c1 1 5 1 6 0" />
                              </svg>
                            )}
                            {d.aiEnabled !== false ? 'ON' : 'OFF'}
                          </button>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                            <button
                              className="btn btn-ghost"
                              title="Threshold"
                              onClick={() => { setThresholdDevIdx(idx); setShowThreshold(true); }}
                              style={{ padding: '5px 8px', display: 'inline-flex', alignItems: 'center', borderRadius: 8 }}
                            >
                              <IconGear />
                            </button>
                            <button
                              className="btn btn-ghost"
                              onClick={() => toggleDeviceStatus(currentPlant, idx)}
                              title={d.st === 'online' ? 'Pause' : 'Start'}
                              style={{ padding: '5px 8px', display: 'inline-flex', alignItems: 'center', borderRadius: 8 }}
                            >
                              {d.st === 'online' ? <IconPause /> : <IconPlay />}
                            </button>
                            <button
                              className="btn btn-ghost"
                              onClick={async () => {
                                if (confirm(`Unlink "${d.name}" from this plant?`)) {
                                  const result = await unlinkDeviceFromPlant(currentPlant, d.sn);
                                  if (result.ok) {
                                    toast(`Unlinked: ${d.name}`, 'success');
                                  } else {
                                    toast(`Unlink failed: ${result.error}`, 'error');
                                  }
                                }
                              }}
                              title="Unlink from plant"
                              style={{ padding: '5px 8px', display: 'inline-flex', alignItems: 'center', borderRadius: 8, color: 'var(--red)' }}
                            >
                              <IconTrashSmall />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
            )}
          </div>
        </div>
      )}

      {/* AI Config Tab */}
      {psTab === 'aiConfig' && isAddonEnabled('ai') && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* AI Configuration */}
          <div style={panelStyle}>
            <div style={headStyle}>{t('aiConfig', lang)}</div>
            <div style={bodyStyle}>
              {/* Master toggle — premium banner */}
              <div
                onClick={() => updatePlantAIConfig(currentPlant, { enabled: !aiConfig.enabled })}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px 18px',
                  borderRadius: 12,
                  background: aiConfig.enabled ? 'var(--green-dim)' : 'var(--red-dim)',
                  border: `1px solid ${aiConfig.enabled ? 'rgba(94,234,160,0.15)' : 'rgba(255,112,134,0.15)'}`,
                  marginBottom: 20,
                  cursor: 'pointer',
                  transition: 'all .3s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: aiConfig.enabled ? 'rgba(94,234,160,0.15)' : 'rgba(255,112,134,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={aiConfig.enabled ? 'var(--green)' : 'var(--red)'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="4" y="4" width="16" height="16" rx="4" /><circle cx="9" cy="10" r="1.5" fill="currentColor" /><circle cx="15" cy="10" r="1.5" fill="currentColor" /><path d="M9 16c1 1 5 1 6 0" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: aiConfig.enabled ? 'var(--green)' : 'var(--red)' }}>
                      {t('aiForPlant', lang)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {devs.filter((d) => d.aiEnabled !== false).length}/{devs.length} devices AI enabled
                    </div>
                  </div>
                </div>
                <ToggleSwitch on={aiConfig.enabled} onToggle={() => updatePlantAIConfig(currentPlant, { enabled: !aiConfig.enabled })} />
              </div>

              {/* Sensitivity */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Anomaly Sensitivity</label>
                <select
                  style={inputStyle}
                  value={aiConfig.sensitivity}
                  onChange={(e) => updatePlantAIConfig(currentPlant, { sensitivity: e.target.value as 'low' | 'medium' | 'high' })}
                >
                  <option value="low">{t('low', lang)}</option>
                  <option value="medium">{t('medium', lang)}</option>
                  <option value="high">{t('high', lang)}</option>
                </select>
              </div>

              {/* Schedule */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>{t('reportSchedule', lang)}</label>
                <select
                  style={inputStyle}
                  value={aiConfig.schedule}
                  onChange={(e) => updatePlantAIConfig(currentPlant, { schedule: e.target.value as 'realtime' | 'daily' | 'weekly' })}
                >
                  <option value="realtime">{t('realtime', lang)}</option>
                  <option value="daily">{t('daily', lang)}</option>
                  <option value="weekly">{t('weekly', lang)}</option>
                </select>
              </div>

              {/* Notification channels — branded cards */}
              <div>
                <label style={labelStyle}>{t('notifications', lang)}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {([
                    // In-App channel hidden for now (per request).
                    { key: 'telegram' as const, label: 'Telegram', color: '#0088cc', bg: '#0088cc18',
                      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0088cc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg> },
                    { key: 'line' as const, label: 'LINE', color: '#06C755', bg: '#06C75518',
                      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#06C755" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg> },
                  ]).map((ch) => (
                    <div
                      key={ch.key}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 14px',
                        borderRadius: 10,
                        background: 'var(--bg-elevated)',
                        border: '1px solid var(--glass-border)',
                        transition: 'all .2s',
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}>
                        <div style={{ width: 26, height: 26, borderRadius: 7, background: ch.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {ch.icon}
                        </div>
                        {ch.label}
                      </span>
                      <ToggleSwitch
                        on={aiConfig.notify[ch.key] ?? false}
                        onToggle={() => {
                          const newNotify = { ...aiConfig.notify, [ch.key]: !aiConfig.notify[ch.key] };
                          updatePlantAIConfig(currentPlant, { notify: newNotify });
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Save — ค่าทั้งการ์ดนี้เก็บลง config_data.aiConfig ของไซต์
                  ก่อนหน้านี้แก้แล้วอยู่แค่ใน memory รีเฟรชก็หาย จึงต้องดู
                  ผลลัพธ์จริงจาก API ไม่ใช่ขึ้น "บันทึกแล้ว" ทุกครั้ง */}
              <button
                disabled={aiSaving}
                onClick={async () => {
                  setAiSaving(true);
                  const r = await savePlantAIConfig(currentPlant);
                  setAiSaving(false);
                  if (r.ok) toast(th ? 'บันทึกการตั้งค่า AI แล้ว' : 'AI settings saved', 'success');
                  else toast(`${t('saveFailed', lang)}: ${r.error || 'error'}`, 'error');
                }}
                style={{
                  width: '100%', marginTop: 20, padding: '11px 22px', fontSize: 14, fontWeight: 800,
                  borderRadius: 10, border: 'none', cursor: aiSaving ? 'wait' : 'pointer', color: '#0C1222',
                  background: 'linear-gradient(135deg, var(--amber), var(--gold))',
                  boxShadow: '0 2px 12px var(--gold-glow)', fontFamily: 'inherit',
                  opacity: aiSaving ? 0.6 : 1,
                }}
              >
                {aiSaving ? (th ? 'กำลังบันทึก…' : 'Saving…') : t('save', lang)}
              </button>
            </div>
          </div>

          {/* Device AI Status — hidden for now (per request) */}
          {false && (
          <div style={panelStyle}>
            <div style={headStyle}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="4" /><circle cx="9" cy="10" r="1.5" fill="currentColor" /><circle cx="15" cy="10" r="1.5" fill="currentColor" /><path d="M9 16c1 1 5 1 6 0" /></svg>
                {t('devicesAIEnabled', lang)}
              </span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--gold-dim)', color: 'var(--gold)' }}>
                {devs.filter((d) => d.aiEnabled !== false).length}/{devs.length}
              </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    {['Device', 'AI', 'Threshold'].map((h) => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {devs.map((d, idx) => (
                    <tr key={d.sn} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{d.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace', marginTop: 1 }}>{d.sn}</div>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <ToggleSwitch
                          on={d.aiEnabled !== false}
                          onToggle={() => currentPlant && toggleDeviceAI(currentPlant, idx)}
                        />
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <button
                          className="btn btn-ghost"
                          onClick={() => { setThresholdDevIdx(idx); setShowThreshold(true); }}
                          style={{ fontSize: 10, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /><circle cx="12" cy="12" r="3" />
                          </svg>
                          {d.thresholds ? Object.keys(d.thresholds).length : 0} rules
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Effluent standards — wastewater sites only. These drive the AI
              water-quality warnings (pH / COD / BOD / DO). */}
          {isWaterPlant && (
            <div style={{ ...panelStyle, gridColumn: '1 / -1', marginBottom: 0 }}>
              <div style={headStyle}>🌊 {th ? 'เกณฑ์มาตรฐานน้ำทิ้ง (AI)' : 'Effluent standards (AI)'}</div>
              <div style={bodyStyle}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
                  {th ? 'ใช้เป็นเกณฑ์แจ้งเตือน AI สำหรับคุณภาพน้ำทิ้งของไซต์นี้' : 'Thresholds the AI uses to flag effluent quality for this site.'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                  {([
                    ['phMin', th ? 'pH ต่ำสุด' : 'pH min', ''],
                    ['phMax', th ? 'pH สูงสุด' : 'pH max', ''],
                    ['codMax', th ? 'COD สูงสุด' : 'COD max', 'mg/L'],
                    ['bodMax', th ? 'BOD สูงสุด' : 'BOD max', 'mg/L'],
                    ['doMin', th ? 'DO ต่ำสุด' : 'DO min', 'mg/L'],
                  ] as [keyof EffluentThresholds, string, string][]).map(([key, label, unit]) => (
                    <div key={key}>
                      <label style={labelStyle}>{label}{unit ? ` (${unit})` : ''}</label>
                      <input
                        style={inputStyle}
                        inputMode="decimal"
                        value={eff[key]}
                        onChange={(e) => { const v = parseFloat(e.target.value); setEff((s) => ({ ...s, [key]: e.target.value === '' ? 0 : (isNaN(v) ? s[key] : v) })); }}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                  <button style={btnStyle} onClick={onSaveEffluent}>
                    <IconSave />
                    {t('save', lang)}
                  </button>
                  <button
                    onClick={() => setEff(DEFAULT_EFFLUENT)}
                    style={{ padding: '9px 18px', fontSize: 13, fontWeight: 700, borderRadius: 10, border: '1px solid var(--glass-border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    {th ? 'ค่าเริ่มต้น' : 'Reset to default'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notifications Tab */}
      {psTab === 'notifications' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Notification Channels */}
          <div style={panelStyle}>
            <div style={headStyle}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
                {t('notifChannels', lang)}
              </span>
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>

              {/* LINE Card — Messaging API (LINE Notify ถูกปิดบริการแล้ว) */}
              {SHOW_CHANNEL.line && (
              <div style={{ padding: 16, borderRadius: 12, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: '#06C75518', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#06C755" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#06C755' }}>LINE</span>
                  </div>
                  <ToggleSwitch on={notifications.channels.line.enabled} onToggle={() => {
                    const ch = { ...notifications.channels };
                    ch.line = { ...ch.line, enabled: !ch.line.enabled };
                    updatePlantNotifications(currentPlant, { ...notifications, channels: ch });
                  }} />
                </div>
                <label style={labelStyle}>{th ? 'ปลายทาง (User / Group ID)' : 'Target (User / Group ID)'}</label>
                <input
                  style={inputStyle}
                  placeholder="Cxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={notifications.channels.line.to || ''}
                  onChange={(e) => {
                    const ch = { ...notifications.channels };
                    ch.line = { ...ch.line, to: e.target.value.trim() };
                    updatePlantNotifications(currentPlant, { ...notifications, channels: ch });
                  }}
                />
                {/*
                  บอกรูปแบบไว้ตรงนี้เลย เพราะสาเหตุที่ระบบเดิมส่ง LINE ล้มเหลว
                  หลักหมื่นครั้งต่อเดือน คือมีคนกรอก "ชื่อกลุ่ม" ลงช่องนี้แทน ID
                */}
                <div style={{ marginTop: 7, fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  {th
                    ? 'ต้องเป็น ID ที่ขึ้นต้นด้วย U (ผู้ใช้) C (กลุ่ม) หรือ R ตามด้วยตัวอักษร 32 ตัว — ไม่ใช่ชื่อกลุ่มหรือ @ID ของ LINE OA · และต้องเชิญบอทเข้ากลุ่มก่อน'
                    : 'Must be an ID starting with U (user), C (group) or R, followed by 32 characters — not a group name or an OA @ID. The bot must be invited to the group first.'}
                </div>

                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    onClick={async () => {
                      const to = (notifications.channels.line.to || '').trim();
                      if (!to) { setLineTest({ state: 'err', msg: th ? 'กรุณากรอกปลายทางก่อน' : 'Enter a target first' }); return; }
                      setLineTest({ state: 'sending' });
                      try {
                        const res = await fetch('/api/notify/line-test', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ to }),
                        });
                        const data = await res.json().catch(() => ({}));
                        if (res.ok && data.ok) setLineTest({ state: 'ok', msg: th ? 'ส่งข้อความทดสอบแล้ว' : 'Test message sent' });
                        else setLineTest({ state: 'err', msg: data.error || (th ? 'ส่งไม่สำเร็จ' : 'Send failed') });
                      } catch {
                        setLineTest({ state: 'err', msg: th ? 'เชื่อมต่อไม่สำเร็จ' : 'Connection failed' });
                      }
                    }}
                    disabled={lineTest.state === 'sending'}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 9,
                      border: '1px solid #06C755', background: 'transparent', color: '#06C755',
                      fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                      cursor: lineTest.state === 'sending' ? 'default' : 'pointer', opacity: lineTest.state === 'sending' ? 0.6 : 1,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg>
                    {lineTest.state === 'sending' ? (th ? 'กำลังส่ง…' : 'Sending…') : (th ? 'ส่งข้อความทดสอบ' : 'Send test message')}
                  </button>
                  {lineTest.state === 'ok' && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--green)' }}>✓ {lineTest.msg}</span>}
                  {lineTest.state === 'err' && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--red)', maxWidth: 420, lineHeight: 1.5 }}>⚠ {lineTest.msg}</span>}
                </div>
              </div>
              )}

              {/* โทรออกแจ้งเตือน — การ์ดแยกเป็นคอมโพเนนต์เพราะต้องโหลด
                  เบอร์จาก tbl_config_notify ตาม mac_id ที่เลือก (ใช้ hook) */}
              {SHOW_CHANNEL.call && <CallAlertCard plant={plant} />}

              {/* Telegram Card */}
              {SHOW_CHANNEL.telegram && (
              <div style={{ padding: 16, borderRadius: 12, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: '#0088cc18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0088cc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#0088cc' }}>Telegram</span>
                  </div>
                  <ToggleSwitch on={notifications.channels.telegram.enabled} onToggle={() => {
                    const ch = { ...notifications.channels };
                    ch.telegram = { ...ch.telegram, enabled: !ch.telegram.enabled };
                    updatePlantNotifications(currentPlant, { ...notifications, channels: ch });
                  }} />
                </div>
                <div>
                  <label style={labelStyle}>Chat ID</label>
                  <input style={inputStyle} placeholder="Enter Chat ID" value={notifications.channels.telegram.chatId} onChange={(e) => {
                    const ch = { ...notifications.channels };
                    ch.telegram = { ...ch.telegram, chatId: e.target.value };
                    updatePlantNotifications(currentPlant, { ...notifications, channels: ch });
                    if (tgTest.state !== 'idle') setTgTest({ state: 'idle' });
                  }} />
                </div>
                {/* Sends via the central M-Tech bot (token server-side) so the
                    site owner can confirm the Chat ID is right in one click. */}
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    onClick={async () => {
                      const chatId = notifications.channels.telegram.chatId?.trim();
                      if (!chatId) { setTgTest({ state: 'err', msg: th ? 'กรุณากรอก Chat ID ก่อน' : 'Enter a Chat ID first' }); return; }
                      setTgTest({ state: 'sending' });
                      try {
                        const res = await fetch('/api/notify/telegram-test', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ chatId }),
                        });
                        const data = await res.json().catch(() => ({}));
                        if (res.ok && data.ok) setTgTest({ state: 'ok', msg: th ? 'ส่งข้อความทดสอบแล้ว' : 'Test message sent' });
                        else setTgTest({ state: 'err', msg: data.error || (th ? 'ส่งไม่สำเร็จ' : 'Send failed') });
                      } catch {
                        setTgTest({ state: 'err', msg: th ? 'เชื่อมต่อไม่สำเร็จ' : 'Connection failed' });
                      }
                    }}
                    disabled={tgTest.state === 'sending'}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 9,
                      border: '1px solid #0088cc', background: 'transparent', color: '#0088cc',
                      fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                      cursor: tgTest.state === 'sending' ? 'default' : 'pointer', opacity: tgTest.state === 'sending' ? 0.6 : 1,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg>
                    {tgTest.state === 'sending' ? (th ? 'กำลังส่ง…' : 'Sending…') : (th ? 'ส่งข้อความทดสอบ' : 'Send test message')}
                  </button>
                  {tgTest.state === 'ok' && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--green)' }}>✓ {tgTest.msg}</span>}
                  {tgTest.state === 'err' && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--red)' }}>⚠ {tgTest.msg}</span>}
                </div>
              </div>
              )}

              {/* Email Card */}
              {SHOW_CHANNEL.email && (
              <div style={{ padding: 16, borderRadius: 12, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--blue-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 4L12 13 2 4" /></svg>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--blue)' }}>Email</span>
                  </div>
                  <ToggleSwitch on={notifications.channels.email.enabled} onToggle={() => {
                    const ch = { ...notifications.channels };
                    ch.email = { ...ch.email, enabled: !ch.email.enabled };
                    updatePlantNotifications(currentPlant, { ...notifications, channels: ch });
                  }} />
                </div>
                <label style={labelStyle}>Recipients</label>
                <input style={inputStyle} placeholder="email@example.com" value={notifications.channels.email.recipients} onChange={(e) => {
                  const ch = { ...notifications.channels };
                  ch.email = { ...ch.email, recipients: e.target.value };
                  updatePlantNotifications(currentPlant, { ...notifications, channels: ch });
                }} />
              </div>
              )}

              {/* Save — notification config lives in the store; this commits the
                  current channels and confirms with a toast. */}
              <button
                onClick={() => {
                  updatePlantNotifications(currentPlant, notifications);
                  toast(th ? 'บันทึกการตั้งค่าแจ้งเตือนแล้ว' : 'Notification settings saved', 'success');
                }}
                style={{
                  width: '100%', marginTop: 4, padding: '11px 22px', fontSize: 14, fontWeight: 800,
                  borderRadius: 10, border: 'none', cursor: 'pointer', color: '#0C1222',
                  background: 'linear-gradient(135deg, var(--amber), var(--gold))',
                  boxShadow: '0 2px 12px var(--gold-glow)', fontFamily: 'inherit',
                }}
              >
                {t('save', lang)}
              </button>

            </div>
          </div>

          {/* Notification Rules */}
          <div style={panelStyle}>
            <div style={headStyle}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
                {t('notifRules', lang)}
                {notifications.rules.length > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: rulesFull ? 'var(--red-dim)' : 'var(--gold-dim)', color: rulesFull ? 'var(--red)' : 'var(--gold)' }}>{notifications.rules.length}/{MAX_NOTIF_RULES}</span>
                )}
              </span>
              <button
                style={{ ...btnStyle, fontSize: 12, padding: '6px 16px', opacity: rulesFull ? 0.5 : 1, cursor: rulesFull ? 'not-allowed' : 'pointer' }}
                disabled={rulesFull}
                title={rulesFull ? (lang === 'th' ? `เพิ่มได้สูงสุด ${MAX_NOTIF_RULES} กฎ` : `Maximum ${MAX_NOTIF_RULES} rules`) : undefined}
                onClick={() => { if (!rulesFull) { setEditRuleIdx(null); setShowAddRule(true); } }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                {t('addRule', lang)}
              </button>
            </div>
            <div style={{ padding: 16 }}>
              {notifications.rules.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3, marginBottom: 12 }}>
                    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                  </svg>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{t('noRules', lang)}</div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>{t('addRuleDesc', lang)}</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {notifications.rules.map((rule, idx) => {
                    const typeConfig: Record<string, { icon: React.ReactNode; color: string; bg: string; label: string }> = {
                      condition: {
                        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
                        color: 'var(--red)', bg: 'var(--red-dim)', label: 'Condition'
                      },
                      interval: {
                        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
                        color: 'var(--blue)', bg: 'var(--blue-dim)', label: 'Interval'
                      },
                      schedule: {
                        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--purple)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
                        color: 'var(--purple)', bg: 'var(--purple-dim)', label: 'Schedule'
                      },
                    };
                    const tc = typeConfig[rule.type] || typeConfig.condition;
                    return (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 14px',
                          borderRadius: 10,
                          background: 'var(--bg-elevated)',
                          border: '1px solid var(--glass-border)',
                          transition: 'all .2s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: tc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {tc.icon}
                          </div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{rule.name}</span>
                              <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: tc.bg, color: tc.color }}>{tc.label}</span>
                              <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: rule.enabled !== false ? 'var(--green-dim)' : 'var(--red-dim)', color: rule.enabled !== false ? 'var(--green)' : 'var(--red)' }}>
                                {rule.enabled !== false ? 'Active' : 'Disabled'}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                              {rule.type === 'condition' && 'channels' in rule ? `${(rule as any).severity || 'warning'} alert` : ''}
                              {rule.type === 'interval' && 'interval' in rule ? `Every ${(rule as any).interval} ${(rule as any).intervalUnit || (rule as any).unit || 'min'}` : ''}
                              {rule.type === 'schedule' && 'time' in rule ? `At ${(rule as any).time}` : ''}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          <button
                            onClick={() => { setEditRuleIdx(idx); setShowAddRule(true); }}
                            className="btn btn-ghost"
                            style={{ padding: '5px 8px', borderRadius: 8, color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center' }}
                            title={t('edit', lang) || 'Edit'}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(t('confirm', lang) + '?')) {
                                const newRules = notifications.rules.filter((_, i) => i !== idx);
                                updatePlantNotifications(currentPlant, { ...notifications, rules: newRules });
                              }
                            }}
                            className="btn btn-ghost"
                            style={{ padding: '5px 8px', borderRadius: 8, color: 'var(--red)', display: 'inline-flex', alignItems: 'center' }}
                          >
                            <IconTrashSmall />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Save — re-commits the current rules to the store + DB (they also
                auto-save on add/edit/delete; this gives an explicit confirm). */}
            <div style={{ padding: '0 16px 16px' }}>
              <button
                onClick={() => {
                  updatePlantNotifications(currentPlant, notifications);
                  toast(th ? 'บันทึกกฎแจ้งเตือนแล้ว' : 'Notification rules saved', 'success');
                }}
                style={{
                  width: '100%', padding: '11px 22px', fontSize: 14, fontWeight: 800,
                  borderRadius: 10, border: 'none', cursor: 'pointer', color: '#0C1222',
                  background: 'linear-gradient(135deg, var(--amber), var(--gold))',
                  boxShadow: '0 2px 12px var(--gold-glow)', fontFamily: 'inherit',
                }}
              >
                {t('save', lang)}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Access Tab */}
      {psTab === 'access' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 20, maxWidth: 640 }}>
          <div style={panelStyle}>
            <div style={headStyle}>
              {lang === 'th' ? 'สิทธิ์เข้าถึงไซต์' : 'Site Access'}
            </div>
            <div style={bodyStyle}>
              {/* Owner (editable — reassign plant ownership) */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
                  {lang === 'th' ? 'เจ้าของ (Owner)' : 'Owner'}
                </div>
                <input
                  type="email"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  placeholder={lang === 'th' ? 'อีเมลเจ้าของ plant' : 'Owner email'}
                  style={inputStyle}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                  {lang === 'th'
                    ? '* เปลี่ยนเจ้าของ = โอนสิทธิ์ owner ของ plant (ต้องเป็นอีเมลผู้ใช้ในระบบ · ห้ามว่าง)'
                    : '* Changing the owner transfers plant ownership (must be a registered user email · cannot be empty)'}
                </div>
              </div>

              {/* Access — single list; permission level follows the user's role
                  in User Management (Admin / Owner / Viewer). */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
                  {lang === 'th' ? 'ผู้เข้าถึง (Access)' : 'Access'}
                </div>
                <MultiEmailSelect
                  value={accessList}
                  onChange={setAccessList}
                  showRole
                  placeholder={lang === 'th' ? 'ค้นหาผู้ใช้เพื่อเพิ่มสิทธิ์เข้าถึง...' : 'Search users to grant access...'}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                  {lang === 'th'
                    ? '* ระดับสิทธิ์ (Admin / Owner / Viewer) กำหนดตาม role ของผู้ใช้ใน User Management'
                    : "* Permission level (Admin / Owner / Viewer) follows the user's role in User Management"}
                </div>
              </div>

              <button
                onClick={saveAccess}
                disabled={!accessDirty || savingAccess}
                className="btn btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: !accessDirty || savingAccess ? 0.5 : 1, cursor: !accessDirty || savingAccess ? 'default' : 'pointer' }}
              >
                <IconSave />
                {savingAccess ? (lang === 'th' ? 'กำลังบันทึก...' : 'Saving...') : t('save', lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      <LinkDeviceModal isOpen={showAddDevice} onClose={() => setShowAddDevice(false)} plantId={currentPlant!} />
      <ThresholdConfigModal isOpen={showThreshold} onClose={() => setShowThreshold(false)} plantId={currentPlant!} deviceIndex={thresholdDevIdx} />
      <NotifRuleModal
        isOpen={showAddRule}
        onClose={() => { setShowAddRule(false); setEditRuleIdx(null); }}
        plantId={currentPlant!}
        editRule={editRuleIdx != null ? notifications.rules[editRuleIdx] : null}
        editIndex={editRuleIdx}
      />
    </div>
  );
}
